/**
 * TabBarVanilla - Vanilla TS Tab Bar for standalone terminal mode.
 * Follows the FileExplorerVanilla pattern: innerHTML rendering, event delegation, dispose.
 */
import type { TabStore, Tab } from '../../stores/tabStore';

export interface TabBarVanillaOptions {
  container: HTMLElement;
  tabStore: TabStore;
  onUpgradeClick?: () => void;
}

// Lucide-style SVG icons
const ICONS = {
  plus: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>',
  x: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
};

export class TabBarVanilla {
  private container: HTMLElement;
  private tabStore: TabStore;
  private onUpgradeClick: (() => void) | undefined;
  private unsubscribe: (() => void) | null = null;
  private editingTabId: string | null = null;

  constructor(options: TabBarVanillaOptions) {
    this.container = options.container;
    this.tabStore = options.tabStore;
    this.onUpgradeClick = options.onUpgradeClick;
    this.setupEventListeners();
    this.unsubscribe = this.tabStore.subscribe(() => this.render());
    this.render();
  }

  private render(): void {
    const tabs = this.tabStore.getTabs();
    const activeTabId = this.tabStore.getActiveTabId();
    const canCreate = this.tabStore.canCreateTab();
    const maxTabs = this.tabStore.getMaxTabs();

    this.container.innerHTML = `
      <div class="tb" role="tablist" aria-label="Terminal tabs">
        <div class="tb-tabs">
          ${tabs.map((tab, i) => this.renderTab(tab, tab.id === activeTabId, i)).join('')}
        </div>
        <div class="tb-actions">
          ${canCreate ? `
            <button class="tb-new-btn" data-action="new-tab" title="New tab (Ctrl+T)" aria-label="New tab">
              ${ICONS.plus}
            </button>
          ` : `
            <span class="tb-limit-hint" title="${maxTabs} tab limit reached">
              <a href="https://app.whss.dev/billing" data-action="upgrade">Upgrade</a>
            </span>
          `}
        </div>
      </div>
    `;

    // If we're editing a tab name, focus the input
    if (this.editingTabId) {
      const input = this.container.querySelector(`.tb-rename-input[data-tab-id="${this.editingTabId}"]`) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }
  }

  private renderTab(tab: Tab, isActive: boolean, index: number): string {
    const activeClass = isActive ? ' active' : '';
    const statusClass = ` status-${tab.status}`;

    if (this.editingTabId === tab.id) {
      return `
        <div class="tb-tab${activeClass}${statusClass}" data-tab-id="${this.esc(tab.id)}" role="tab" aria-selected="${isActive}" tabindex="${isActive ? '0' : '-1'}">
          ${tab.hasActivity ? '<span class="tb-activity" aria-label="New activity">●</span>' : ''}
          <input
            class="tb-rename-input"
            data-tab-id="${this.esc(tab.id)}"
            value="${this.esc(tab.name)}"
            spellcheck="false"
            aria-label="Rename tab"
          />
          ${this.renderCloseButton(tab.id)}
        </div>
      `;
    }

    const shortcutHint = index < 9 ? ` (Ctrl+${index + 1})` : '';
    return `
      <div class="tb-tab${activeClass}${statusClass}" data-tab-id="${this.esc(tab.id)}" role="tab" aria-selected="${isActive}" tabindex="${isActive ? '0' : '-1'}" title="${this.esc(tab.name)}${shortcutHint}">
        ${tab.hasActivity ? '<span class="tb-activity" aria-label="New activity">●</span>' : ''}
        <span class="tb-tab-name" data-tab-id="${this.esc(tab.id)}">${this.esc(tab.name)}</span>
        ${this.renderCloseButton(tab.id)}
      </div>
    `;
  }

  private renderCloseButton(tabId: string): string {
    // Only show close button when there are multiple tabs
    if (this.tabStore.getTabs().length <= 1) return '';
    return `<button class="tb-close-btn" data-action="close" data-tab-id="${this.esc(tabId)}" title="Close tab" aria-label="Close tab">${ICONS.x}</button>`;
  }

  private esc(str: string): string {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  private setupEventListeners(): void {
    this.container.addEventListener('click', this.handleClick.bind(this));
    this.container.addEventListener('dblclick', this.handleDoubleClick.bind(this));
    this.container.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  private handleClick(e: Event): void {
    const target = e.target as HTMLElement;

    // New tab button
    if (target.closest('[data-action="new-tab"]')) {
      this.tabStore.createTab();
      return;
    }

    // Upgrade link
    if (target.closest('[data-action="upgrade"]')) {
      e.preventDefault();
      if (this.onUpgradeClick) {
        this.onUpgradeClick();
      } else {
        window.open('https://app.whss.dev/billing', '_blank');
      }
      return;
    }

    // Close button
    const closeBtn = target.closest('[data-action="close"]') as HTMLElement;
    if (closeBtn) {
      e.stopPropagation();
      const tabId = closeBtn.dataset.tabId;
      if (tabId) this.tabStore.closeTab(tabId);
      return;
    }

    // Tab click (switch)
    const tabEl = target.closest('.tb-tab') as HTMLElement;
    if (tabEl) {
      const tabId = tabEl.dataset.tabId;
      if (tabId && tabId !== this.editingTabId) {
        this.tabStore.switchTab(tabId);
      }
    }
  }

  private handleDoubleClick(e: Event): void {
    const target = e.target as HTMLElement;
    const nameEl = target.closest('.tb-tab-name') as HTMLElement;
    if (nameEl) {
      const tabId = nameEl.dataset.tabId;
      if (tabId) {
        this.editingTabId = tabId;
        this.render();
      }
    }
  }

  private handleKeydown(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;

    // Handle rename input
    if (target.classList.contains('tb-rename-input')) {
      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        const tabId = (target as HTMLInputElement).dataset.tabId;
        if (tabId && e.key === 'Enter') {
          this.tabStore.renameTab(tabId, (target as HTMLInputElement).value);
        }
        this.editingTabId = null;
        this.render();
        return;
      }
    }

    // Handle blur on rename input (via focusout)
    if (target.classList.contains('tb-rename-input') && e.type === 'focusout') {
      const tabId = (target as HTMLInputElement).dataset.tabId;
      if (tabId) {
        this.tabStore.renameTab(tabId, (target as HTMLInputElement).value);
      }
      this.editingTabId = null;
      this.render();
    }
  }

  /**
   * Initialize the tab bar. Call after construction to set up blur handler.
   */
  initialize(): void {
    // Listen for blur on rename inputs (focusout bubbles, blur doesn't)
    this.container.addEventListener('focusout', (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('tb-rename-input')) {
        const tabId = (target as HTMLInputElement).dataset.tabId;
        if (tabId && this.editingTabId === tabId) {
          this.tabStore.renameTab(tabId, (target as HTMLInputElement).value);
          this.editingTabId = null;
          this.render();
        }
      }
    });
  }

  dispose(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.container.innerHTML = '';
  }
}
